
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCourierInfo } from '@/pages/courier/hooks/useCourierInfo';
import { getPaymentMethodInfo, unlinkPaymentMethod } from '@/utils/supabase/courier/paymentMethodOperations';
import { toast } from '@/components/ui/sonner';

export interface PaymentMethodInfo {
  paymentMethodId: string;
  cardLast4: string | null;
  cardType: string | null;
  expiryMonth: string | null;
  expiryYear: string | null;
  updatedAt: string | null;
}

export const usePaymentMethods = () => {
  const { user } = useAuth();
  const { courierId, loading: courierLoading } = useCourierInfo();
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodInfo | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Получение информации о платежном методе
  const fetchPaymentMethod = async () => {
    if (!courierId) {
      console.log('ID курьера не определен, пропускаем запрос платежного метода');
      return;
    }

    setLoading(true);
    try {
      console.log('Получение платежного метода для курьера:', courierId);
      
      const { success, data, error } = await getPaymentMethodInfo(courierId);
      
      if (success && data) {
        console.log('Получены данные платежного метода:', data);
        setPaymentMethod(data);
      } else {
        console.log('Платежный метод не найден или произошла ошибка');
        setPaymentMethod(null);
        if (error) {
          console.error('Ошибка при получении платежного метода:', error);
        }
      }
    } catch (error) {
      console.error('Ошибка при получении платежного метода:', error);
    } finally {
      setLoading(false);
    }
  };

  // Функция для принудительного обновления данных
  const refreshPaymentMethod = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Отвязка платежного метода
  const handleUnlinkPaymentMethod = async () => {
    if (!courierId) {
      toast.error('Не удалось определить ID курьера');
      return false;
    }

    setLoading(true);
    try {
      const { success, error } = await unlinkPaymentMethod(courierId);
      
      if (success) {
        toast.success('Карта успешно отвязана');
        setPaymentMethod(null);
        return true;
      } else {
        toast.error('Не удалось отвязать карту', {
          description: error?.message || 'Произошла ошибка'
        });
        return false;
      }
    } catch (error: any) {
      toast.error('Не удалось отвязать карту', {
        description: error?.message || 'Произошла ошибка'
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Загрузка данных при изменении courierId или обновлении
  useEffect(() => {
    // Если courierId определен и загрузка курьера завершена, получаем платежный метод
    if (courierId && !courierLoading) {
      console.log('Запуск загрузки платежного метода. ID курьера:', courierId);
      fetchPaymentMethod();
    } else if (courierLoading) {
      console.log('Ожидание загрузки данных курьера...');
      // Сбрасываем состояние загрузки, если ID курьера не определен
      setLoading(true);
    } else {
      console.log('ID курьера не определен после загрузки данных курьера');
      setLoading(false);
    }
  }, [courierId, courierLoading, refreshTrigger]);

  return {
    paymentMethod,
    loading,
    fetchPaymentMethod,
    refreshPaymentMethod,
    unlinkPaymentMethod: handleUnlinkPaymentMethod
  };
};
